import { memo, useEffect, useState } from 'react';

import {
  Box,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { useGetKaapClusters } from '../../../hooks';
import { KaapClusterNodeGroupValue } from '../types';

const CLUSTER_GROUP_NAMES: Record<string, string> = {
  'dev-playground': 'Dev Playground - Experimental',
  'dev-internal': 'Dev Internal - For development environments',
  'prod-internal': 'Prod Internal - For internal production services',
  'prod-external':
    'Prod External - Only for production applications serving TomTom commercial clients',
};

const getClusterGroupName = (stage?: string, tier?: string): string => {
  if (!stage || !tier) {
    return tier ?? '';
  }
  return CLUSTER_GROUP_NAMES[`${stage}-${tier}`] ?? tier;
};

const capitalizeFirstLetter = (value: string) =>
  value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);

const TITLE = 'Cluster';

type ClusterSectionProps = {
  value: KaapClusterNodeGroupValue;
  stage: string;
  onChange: (value: KaapClusterNodeGroupValue) => void;
  disabled?: boolean;
};

export const ClusterSection = memo(
  ({ value, stage, onChange, disabled }: ClusterSectionProps) => {
    const { value: clusters, loading } = useGetKaapClusters();

    const stageClusters = (clusters ?? []).filter(
      c => !stage || c.stage === stage,
    );

    const selectedCluster =
      stageClusters.find(c => c.id === value?.clusterId) ?? null;

    const [selectedNodeGroups, setSelectedNodeGroups] = useState<string[]>(
      value?.nodeGroups ?? [],
    );

    useEffect(() => {
      if (!loading && value?.clusterId && !selectedCluster) {
        setSelectedNodeGroups([]);
        onChange({ clusterId: '', tier: '', nodeGroups: [] });
      }
    }, [stage, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClusterChange = (_: any, cluster: typeof selectedCluster) => {
      setSelectedNodeGroups([]);
      if (cluster) {
        onChange({ clusterId: cluster.id, tier: cluster.tier, nodeGroups: [] });
      } else {
        onChange({ clusterId: '', tier: '', nodeGroups: [] });
      }
    };

    const handleNodeGroupsChange = (_: any, nodeGroups: string[]) => {
      setSelectedNodeGroups(nodeGroups);
      if (selectedCluster) {
        onChange({
          clusterId: selectedCluster.id,
          tier: selectedCluster.tier,
          nodeGroups,
        });
      }
    };

    useEffect(() => {
      if (value?.nodeGroups) {
        setSelectedNodeGroups(value.nodeGroups);
      }
    }, [value?.clusterId, value?.nodeGroups]); // eslint-disable-line react-hooks/exhaustive-deps

    const availableNodeGroups = selectedCluster?.nodeGroups ?? [];

    const nodeGroupsControl = (
      <FormControl fullWidth required error={selectedNodeGroups.length === 0}>
        <Autocomplete
          id="cluster-nodegroups"
          multiple
          size="small"
          value={selectedNodeGroups}
          options={availableNodeGroups}
          getOptionLabel={option => option}
          onChange={handleNodeGroupsChange}
          renderInput={params => (
            <>
              <input
                tabIndex={-1}
                autoComplete="off"
                style={{ opacity: 0, height: 0, padding: 0, margin: 0 }}
                value={selectedNodeGroups.length ? 'ok' : ''}
                required
                readOnly
              />
              <TextField
                {...params}
                label="Node Groups*"
                size="small"
                variant="outlined"
                InputProps={params.InputProps}
                error={selectedNodeGroups.length === 0}
              />
            </>
          )}
          noOptionsText="No node groups available for this cluster"
        />
        {selectedCluster &&
          (selectedNodeGroups.length === 0 ? (
            <FormHelperText error>Node groups required</FormHelperText>
          ) : (
            <FormHelperText>
              Select node groups the tenant will use
            </FormHelperText>
          ))}
      </FormControl>
    );

    if (disabled) {
      const clusterLabel = selectedCluster?.id ?? value?.clusterId ?? '';
      const tierLabel = capitalizeFirstLetter(
        selectedCluster?.tier ?? value?.tier ?? '',
      );

      return (
        <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
          <Typography variant="h6">Cluster and Node Groups</Typography>
          <FormControl fullWidth>
            <TextField
              id="cluster-tier"
              label="Tier"
              size="small"
              value={tierLabel}
              disabled
              inputProps={{ readOnly: true }}
            />
          </FormControl>
          <FormControl fullWidth>
            <TextField
              id="cluster"
              label={TITLE}
              size="small"
              variant="outlined"
              value={clusterLabel}
              disabled
              inputProps={{ readOnly: true }}
            />
          </FormControl>
          {nodeGroupsControl}
        </Box>
      );
    }

    return (
      <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
        <Typography variant="h6">Cluster and Node Groups</Typography>
        <FormControl fullWidth required error={!value?.clusterId}>
          <Autocomplete
            id="cluster"
            size="small"
            value={selectedCluster}
            loading={loading}
            options={[...stageClusters].sort(
              (a, b) =>
                b.tier.localeCompare(a.tier) || b.name.localeCompare(a.name),
            )}
            groupBy={option => getClusterGroupName(option.stage, option.tier)}
            getOptionLabel={option => option.id}
            getOptionSelected={(option, val) => option.id === val.id}
            onChange={handleClusterChange}
            renderOption={option => (
              <Box>
                <Typography variant="body2">{option.id}</Typography>
              </Box>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label={TITLE}
                size="small"
                required
                variant="outlined"
                InputProps={params.InputProps}
                error={!value?.clusterId}
              />
            )}
            noOptionsText={
              stage
                ? `No clusters found for stage "${stage}"`
                : 'No clusters found'
            }
            loadingText="Loading clusters..."
          />
        </FormControl>

        {selectedCluster && nodeGroupsControl}
      </Box>
    );
  },
);
